import { useNavigate, useSearch } from "@tanstack/react-router";
import { useEffect } from "react";

import { FilterBar, type FilterSpec } from "@/components/data";
import { applyFilters, type FilterValues } from "@/lib/filters";

import type { FilterOptions, Page } from "../api";
import { readShared } from "../filters";
import { useTabQuery } from "../PageFrame";
import type { SourcingTab } from "../routes";
import { useFilterOptions } from "../SharedFilterBar";
import { landedExtra, pageFilter, pageFilterList } from "./params";

export type PageFilterKind =
  | "feature"
  | "criterion"
  | "criteria_phase"
  | "cost_type"
  | "incoterm"
  | "incoterm_short"
  | "order_size"
  | "variant"
  | "destination"
  | "origin"
  | "year"
  | "stall"
  | "sample"
  | "evaluator";

/** The second filter row of each page, in mockup order. Tabs not listed have none. */
export const FILTERS_BY_TAB: Record<string, readonly PageFilterKind[]> = {
  ranking_table: ["criteria_phase", "sample", "evaluator"],
  stalled: ["stall"],
  feature_ranking: ["feature"],
  feature_performance_average: ["feature"],
  feature_performance_minimum: ["feature"],
  criteria_ranking: ["criterion", "criteria_phase", "sample", "evaluator"],
  criteria_catalog: ["feature", "criteria_phase"],
  sourcing_funnel: ["cost_type", "incoterm_short", "order_size"],
  ingredients_funnel: ["variant", "incoterm"],
  monthly_offers_purchases: ["variant", "incoterm", "year"],
  landed_cost_by_offer: ["variant", "destination", "incoterm", "origin"],
  landed_cost_vs_purchase: ["variant", "destination", "incoterm", "origin"],
};

export const VARIANT_TABS: readonly string[] = [
  "ingredients_funnel",
  "landed_cost_by_offer",
  "landed_cost_vs_purchase",
  "monthly_offers_purchases",
];
export const LANDED_TABS: readonly string[] = ["landed_cost_by_offer", "landed_cost_vs_purchase"];

export const INCOTERMS: readonly string[] = ["EXW", "FCA", "FOB", "CIF", "CIP", "DAP", "DDP"];
export const INCOTERMS_SHORT: readonly string[] = ["EXW", "DAP", "DDP"];
export const DEFAULT_COST_TYPE = "Total Yearly Recurring Cost";
/** The oldest quote in the funnel is from 2016. */
export const FIRST_QUOTE_YEAR = 2016;
export const STALL_CHOICES = [
  { value: "30", label: "30+ days" },
  { value: "60", label: "60+ days" },
] as const;

export interface FilterContext {
  options: FilterOptions | null;
  /** The chosen item, or null for all items. */
  item: string | null;
  /** Origin codes found in the page's unfiltered rows (landed-cost pages only). */
  origins: string[];
  thisYear: number;
}

const choices = (values: readonly string[]) => values.map((value) => ({ value, label: value }));

/** With an item chosen the list is that item's own features; otherwise every feature. */
export function featureOptions(context: FilterContext): string[] {
  const options = context.options;
  if (!options) return [];
  if (context.item && options.criteria.length > 0) {
    return Array.from(new Set(options.criteria.map((criterion) => criterion.feature))).sort();
  }
  return options.features;
}

export function pageFilterSpecs(tabKey: string, context: FilterContext): FilterSpec[] {
  const options = context.options;
  const off = options === null;
  return (FILTERS_BY_TAB[tabKey] ?? []).map((kind): FilterSpec => {
    switch (kind) {
      case "feature":
        return { name: "feature", label: "Feature", kind: "choice", anyLabel: "All features", disabled: off, options: choices(featureOptions(context)) };
      case "criterion":
        return {
          name: "criterion",
          label: "Criteria",
          kind: "choice",
          anyLabel: "All",
          disabled: off,
          options: choices((options?.criteria ?? []).map((criterion) => criterion.criterion)),
        };
      case "criteria_phase":
        return { name: "criteria_phase", label: tabKey === "ranking_table" ? "Phase" : "Criteria phase", kind: "choice", anyLabel: "All", disabled: off, options: choices(options?.criteria_phases ?? []) };
      case "cost_type":
        return {
          name: "cost_type",
          label: "Cost type",
          kind: "choice",
          anyLabel: DEFAULT_COST_TYPE,
          disabled: off,
          options: choices((options?.cost_types ?? []).filter((costType) => costType !== DEFAULT_COST_TYPE)),
        };
      case "incoterm":
        return { name: "incoterm", label: "Incoterm", kind: "choice", anyLabel: "All", options: choices(INCOTERMS) };
      case "incoterm_short":
        return { name: "incoterm", label: "Incoterms", kind: "choice", anyLabel: "All", options: choices(INCOTERMS_SHORT) };
      case "order_size":
        return { name: "order_size", label: "Order size", kind: "choice", anyLabel: "Current volume", options: [] };
      case "variant": {
        const variants = options?.variants ?? [];
        // A variant page always has a variant: the first one is the "no choice" label, the rest are the options.
        return { name: "variant", label: "Variant", kind: "choice", anyLabel: variants[0] ?? "None", disabled: off || variants.length === 0, options: choices(variants.slice(1)) };
      }
      case "destination":
        return { name: "destination", label: "Destination", kind: "choice", anyLabel: "UK", options: choices(["EU"]) };
      case "origin":
        return { name: "origin", label: "Origin ISO", kind: "choice", anyLabel: "All", options: choices(context.origins) };
      case "year": {
        const years: string[] = [];
        for (let year = context.thisYear; year >= FIRST_QUOTE_YEAR; year -= 1) years.push(String(year));
        return { name: "year", label: "Year", kind: "choice", anyLabel: "All", options: choices(years) };
      }
      case "stall":
        return { name: "stall", label: "No movement for", kind: "choice", anyLabel: "14+ days", options: [...STALL_CHOICES] };
      case "sample":
        // v1.7 (Ioan 3 Nov): one entry per option x sample, "Option – Sample", several at once.
        return {
          name: "sample",
          label: "Sample",
          kind: "multi",
          anyLabel: "All",
          disabled: off,
          options: (options?.sample_picks ?? []).map((pick) => ({ value: pick.key, label: pick.label })),
        };
      case "evaluator":
        return { name: "evaluator", label: "Reviewed by", kind: "choice", anyLabel: "All", disabled: off, options: choices(options?.evaluators ?? []) };
    }
  });
}

/** A page-filter change keeps every other search key and returns to page 1. */
export function pageFilterSearch(previous: Record<string, unknown>, next: FilterValues): Record<string, unknown> {
  const out = applyFilters(previous, next);
  delete out.page;
  delete out.lpage;
  return out;
}

/**
 * Page filters whose URL value is not one of the pill's options: the pill would read its "all" label
 * while the value still filtered the page. They come from another item, variant or page (a link, a tab
 * switch, a shared URL). A pill that is off or has no list yet is left alone; the variant has its own
 * rule (`variantDefault`).
 */
export function staleFilters(specs: FilterSpec[], values: FilterValues): string[] {
  return specs
    .filter((spec) => {
      const value = values[spec.name] ?? null;
      const known = spec.options ?? [];
      if (spec.name === "variant" || spec.disabled || value === null || known.length === 0) return false;
      const picks = Array.isArray(value) ? value : [value];
      return picks.some((pick) => !known.some((option) => option.value === pick));
    })
    .map((spec) => spec.name);
}

/** The item a page's filter row works with: none on a tab that does not take the Item filter. */
export const filterItem = (tab: SourcingTab, item: string | null): string | null => (tab.accepts.includes("item") ? item : null);

/** The first entry of the Variant pill stands for the item's first variant, never for "no variant". */
export function withVariant(next: FilterValues, first: string | null): FilterValues {
  return "variant" in next && (next.variant ?? null) === null && first !== null ? { ...next, variant: first } : next;
}

/**
 * The variant a variant page must switch to: the item's first one when none is chosen, or when the
 * chosen one is not the item's (a link or a tab switch carried it from another item). Null = leave as is.
 */
export function variantDefault(tabKey: string, variant: string | null, variants: string[] | null): string | null {
  if (!VARIANT_TABS.includes(tabKey) || !variants || variants.length === 0) return null;
  return variant === null || !variants.includes(variant) ? variants[0] : null;
}

export function distinctOrigins(rows: Array<{ origin_iso: string | null }>): string[] {
  return Array.from(new Set(rows.map((row) => row.origin_iso).filter((origin): origin is string => !!origin))).sort();
}

function FilterRow({ tab, origins }: { tab: SourcingTab; origins: string[] }) {
  const search = useSearch({ strict: false }) as Record<string, unknown>;
  const navigate = useNavigate();
  const item = filterItem(tab, readShared(search).item);
  const filterOptions = useFilterOptions(item);
  const options = filterOptions.data?.data ?? null;
  const variant = pageFilter(search, "variant");
  const firstVariant = options?.variants[0] ?? null;
  const nextVariant = variantDefault(tab.tabKey, variant, options?.variants ?? null);
  const specs = pageFilterSpecs(tab.tabKey, { options, item, origins, thisYear: new Date().getFullYear() });
  const values: FilterValues = {};
  for (const spec of specs) {
    values[spec.name] = spec.kind === "multi" ? pageFilterList(search, spec.name as never) : pageFilter(search, spec.name as never);
  }
  const stale = staleFilters(specs, values).join(",");

  // Put the URL right in one replace: the variant a variant page needs, and no filter its pill cannot show.
  useEffect(() => {
    if (nextVariant === null && stale === "") return;
    const repair: FilterValues = {};
    for (const name of stale.split(",").filter(Boolean)) repair[name] = null;
    if (nextVariant !== null) repair.variant = nextVariant;
    void navigate({
      search: ((previous: Record<string, unknown>) => applyFilters(previous, repair)) as never,
      replace: true,
    } as never);
  }, [nextVariant, stale, navigate]);

  // An item with no variant has nothing to pick on a variant page: the page says "Ingredients only." and the row is left out.
  if (VARIANT_TABS.includes(tab.tabKey) && item !== null && options !== null && options.variants.length === 0) return null;
  if (specs.length === 0) return null;
  return (
    <div className="border-t border-dashed border-ks-grey-3 pt-2">
      <FilterBar
        filters={specs}
        values={values}
        layout="inline"
        searchable
        defaultValues={{ variant: values.variant ?? null }}
        onChange={(next) =>
          void navigate({
            search: ((previous: Record<string, unknown>) => pageFilterSearch(previous, withVariant(next, firstVariant))) as never,
          } as never)
        }
      />
    </div>
  );
}

/** The landed-cost pages read their origin list from the same rows the page shows (one shared request). */
function LandedFilterRow({ tab }: { tab: SourcingTab }) {
  const search = useSearch({ strict: false }) as Record<string, unknown>;
  const rows = useTabQuery<Page<{ origin_iso: string | null }>>(tab, landedExtra(tab.tabKey, search));
  return <FilterRow tab={tab} origins={distinctOrigins(rows.data?.data?.items ?? [])} />;
}

export function PageFilters({ tab }: { tab: SourcingTab }) {
  if (!(tab.tabKey in FILTERS_BY_TAB)) return null;
  if (LANDED_TABS.includes(tab.tabKey)) return <LandedFilterRow tab={tab} />;
  return <FilterRow tab={tab} origins={[]} />;
}
